import {
  Dashboard,
  DashboardComponentNames,
  IDashboardComponentProperties,
  IDashboardDataGridProperties,
  IFlow,
  ILayout,
  IProperty,
  LOCAL_STORAGE_DASHBOARD_VIEW_MODE_KEY,
  localForageUtil,
  makeDynamicReportingPageStore,
  PageViewModeEnum,
  SortModel,
  useSidePeekDrawer,
  useDisableLastVisitedWork,
  useWorkVersionDrawer,
  SIDE_PEEK_ID_QUERY_PARAM,
  getDashboardSidePanelKey,
} from '@diana/core';
import { Grid } from '@mui/material';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { forwardRef, Fragment, useEffect, useState } from 'react';
import { ReportingTable } from './ReportingTable';
import { PreselectFiltersModal } from './PreselectFiltersModal';
import { useSearchParams } from 'react-router-dom';
import { GridSortModel } from '@mui/x-data-grid-premium';

const FROM_EMAIL_QUERY_PARAM = 'fromEmail';

export const Content = forwardRef<
  GridApiPremium,
  {
    reportId: string;
    layoutData: ILayout<
      IDashboardComponentProperties | IDashboardDataGridProperties,
      DashboardComponentNames
    >;
    flows: IFlow[];
    forceEmptySelection: boolean;
    supportedProperties: IProperty[];
    onSortModelChange: (model: GridSortModel) => void;
    sortModel: SortModel;
  }
>((props, forwardedRef) => {
  const [searchParams] = useSearchParams();
  const {
    reportId,
    layoutData,
    flows,
    forceEmptySelection,
    supportedProperties,
    onSortModelChange,
    sortModel,
  } = props;
  const fromEmailRedirected = searchParams.get(FROM_EMAIL_QUERY_PARAM);
  const [isPreselectFilterModalOpen, setIsPreselectFilterModalOpen] = useState<boolean>(
    (layoutData.showPreselectFiltersModal && !fromEmailRedirected) ?? false,
  );
  const useReportingPageStore = makeDynamicReportingPageStore(reportId);
  const viewMode = useReportingPageStore((store) => store.viewMode);
  const setViewMode = useReportingPageStore((store) => store.setViewMode);
  const disableLastVisited = useDisableLastVisitedWork(layoutData as unknown as ILayout);

  const closePreselectFilterModal = () => {
    setIsPreselectFilterModalOpen(false);
  };

  useWorkVersionDrawer();
  const { openDashboardDrawer } = useSidePeekDrawer();

  useEffect(() => {
    localForageUtil
      .getItem<PageViewModeEnum>({
        key: `${LOCAL_STORAGE_DASHBOARD_VIEW_MODE_KEY}-${reportId}`,
      })
      .then((storedViewMode) => {
        storedViewMode && setViewMode(storedViewMode);
      });

    localForageUtil
      .getItem<string>({ key: getDashboardSidePanelKey(reportId) })
      .then((openedSidePanelId) => {
        const workId = (openedSidePanelId || searchParams.get(SIDE_PEEK_ID_QUERY_PARAM)) ?? null;

        if (workId && layoutData?.sidePanelLayoutId && !isPreselectFilterModalOpen) {
          openDashboardDrawer({
            workId,
            sidePanelLayoutId: layoutData.sidePanelLayoutId,
            reportId,
          });
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, isPreselectFilterModalOpen]);

  if (isPreselectFilterModalOpen)
    return (
      <PreselectFiltersModal
        isOpen={isPreselectFilterModalOpen}
        closeModal={closePreselectFilterModal}
        reportId={reportId}
        displayName={layoutData.displayName}
        layoutData={layoutData}
        flows={flows}
      />
    );

  return (
    <Fragment>
      {viewMode === PageViewModeEnum.Board && layoutData.components.length > 0 ? (
        <Dashboard reportId={reportId} layout={layoutData} flows={flows} />
      ) : (
        <Grid>
          <ReportingTable
            supportedProperties={supportedProperties}
            ref={forwardedRef}
            reportId={reportId}
            flows={flows}
            layout={layoutData}
            disableLastVisited={disableLastVisited}
            forceEmptySelection={forceEmptySelection}
            sortModel={sortModel}
            onSortModelChange={onSortModelChange}
          />
        </Grid>
      )}
    </Fragment>
  );
});
